import React, { useState, useEffect } from 'react';
import { useGroups } from '../hooks/useGroups';
import { useUsers } from '../hooks/useUsers';
import GroupTable from './GroupTable';
import CreateGroupModal from './CreateGroupModal';
import GroupSearchModal from './GroupSearchModal';
import AddSubgroupModal from './AddSubgroupModal';
import RemoveUserFromGroupModal from './RemoveUserFromGroupModal';
import SearchBar from '../shared/SearchBar';
import LoadingSpinner from '../shared/LoadingSpinner';
import ErrorAlert from '../shared/ErrorAlert';
import './GroupManagement.css';

const GroupManagement = () => {
  const {
    groups,
    loading,
    error,
    fetchGroups,
    createGroup,
    addSubgroup,
    removeUserFromGroup,
    searchGroupByName
  } = useGroups();

  const { users } = useUsers();

  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [showSubgroupModal, setShowSubgroupModal] = useState(false);
  const [showRemoveUserModal, setShowRemoveUserModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  const handleSearch = async (term) => {
    setSearchTerm(term);
    if (term.trim()) {
      setSearchLoading(true);
      try {
        const results = await searchGroupByName(term);
        setSearchResults(results);
      } catch (error) {
        console.error('Search failed:', error);
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    } else {
      setSearchResults([]);
    }
  };

  const handleCreateGroup = async (groupData) => {
    try {
      await createGroup(groupData);
      setShowCreateModal(false);
      await fetchGroups(); // Refresh the list
    } catch (error) {
      console.error('Failed to create group:', error);
    }
  };

  const handleAddSubgroup = async (parentGroupName, subgroupName) => {
    try {
      await addSubgroup(parentGroupName, subgroupName);
      setShowSubgroupModal(false);
      setSelectedGroup(null);
      await fetchGroups(); // Refresh the list
    } catch (error) {
      console.error('Failed to add subgroup:', error);
    }
  };

  const handleRemoveUserFromGroup = async (groupName, userId) => {
    try {
      await removeUserFromGroup(groupName, userId);
      setShowRemoveUserModal(false);
      setSelectedGroup(null);
      await fetchGroups(); // Refresh the list
    } catch (error) {
      console.error('Failed to remove user from group:', error);
    }
  };

  const openAddSubgroupModal = (group) => {
    setSelectedGroup(group);
    setShowSubgroupModal(true);
  };

  const openRemoveUserModal = (group) => {
    setSelectedGroup(group);
    setShowRemoveUserModal(true);
  };

  const displayGroups = searchTerm.trim() ? searchResults : groups;

  return (
    <div className="group-management">
      <div className="group-management-header">
        <div className="header-left">
          <h2>Group Management</h2>
          <p className="subtitle">Manage FreeIPA groups and memberships</p>
        </div>
        <div className="header-actions">
          <button 
            className="btn btn-secondary"
            onClick={() => setShowSearchModal(true)}
          >
            Advanced Search
          </button>
          <button 
            className="btn btn-primary"
            onClick={() => setShowCreateModal(true)}
          >
            Create Group
          </button>
        </div>
      </div>

      <div className="group-management-controls">
        <SearchBar
          value={searchTerm}
          onChange={handleSearch}
          placeholder="Search groups by name..."
          loading={searchLoading}
        />
        
        {searchTerm.trim() && (
          <div className="search-info">
            <span className="search-results-count">
              {searchResults.length} group(s) found for "{searchTerm}"
            </span>
            <button 
              className="btn btn-link"
              onClick={() => {
                setSearchTerm('');
                setSearchResults([]);
              }}
            >
              Clear Search
            </button>
          </div>
        )}
      </div>

      {error && <ErrorAlert message={error} />}

      {loading ? (
        <LoadingSpinner message="Loading groups..." />
      ) : (
        <GroupTable
          groups={displayGroups}
          onAddSubgroup={openAddSubgroupModal}
          onRemoveUser={openRemoveUserModal}
          onRefresh={fetchGroups}
        />
      )}

      {showCreateModal && (
        <CreateGroupModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateGroup}
        />
      )}

      {showSearchModal && (
        <GroupSearchModal
          onClose={() => setShowSearchModal(false)}
          onSearchByName={searchGroupByName}
        />
      )}

      {showSubgroupModal && selectedGroup && (
        <AddSubgroupModal
          parentGroup={selectedGroup}
          availableGroups={groups.filter(g => g.cn !== selectedGroup.cn)}
          onClose={() => {
            setShowSubgroupModal(false);
            setSelectedGroup(null);
          }}
          onSubmit={handleAddSubgroup}
        />
      )}

      {showRemoveUserModal && selectedGroup && (
        <RemoveUserFromGroupModal
          group={selectedGroup}
          users={users}
          onClose={() => {
            setShowRemoveUserModal(false);
            setSelectedGroup(null);
          }}
          onSubmit={handleRemoveUserFromGroup}
        />
      )}
    </div>
  );
};

export default GroupManagement;
